import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getOrganizationContext, setOrganizationContext } from '../orgContext';
import config from '../config';
import './AdminLayout.css';
import { useAuth0 } from '@auth0/auth0-react';
import { adminNavigationSections, filterNavSections, getMembershipRoles, getUserRoles, mergeRoles } from '../navigationConfig';
import AdminSidebarNavigation from './AdminSidebarNavigation';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, user, logout } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [organizationDisplayName, setOrganizationDisplayName] = useState('');
  const [userDisplayName, setUserDisplayName] = useState('');
  const [membershipRoles, setMembershipRoles] = useState<string[]>([]);
  const [membershipResolved, setMembershipResolved] = useState(false);
  const organizationContext = getOrganizationContext();
  const userRoles = useMemo(() => mergeRoles(getUserRoles(user), membershipRoles), [membershipRoles, user]);
  const visibleSections = useMemo(() => filterNavSections(adminNavigationSections, isAuthenticated, userRoles), [isAuthenticated, userRoles]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    const context = getOrganizationContext();
    const shouldRequireOrganization =
      !location.pathname.startsWith('/manage-categories-v2') &&
      !(location.pathname === '/manage-schedule' || location.pathname.startsWith('/manage-schedule/'));

    if (!membershipResolved) {
      return;
    }

    if (shouldRequireOrganization && !context?.organizationId) {
      navigate('/analytics', { replace: true });
    }
  }, [isAuthenticated, isLoading, location.pathname, membershipResolved, navigate]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const loadOrganizationDisplayName = async () => {
      if (!isAuthenticated || !organizationContext?.organizationId) {
        setOrganizationDisplayName('No organization');
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${config.apiBaseUrl}/api/organizations/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Unable to load organization details');
        }

        const organizations = await response.json();
        if (Array.isArray(organizations)) {
          const matchedOrganization = organizations.find((item: any) =>
            Number(item.organizationId) === organizationContext.organizationId ||
            String(item.organizationSlug || '').toLowerCase() === organizationContext.organizationSlug.toLowerCase()
          );

          const resolvedName = matchedOrganization?.organizationName || matchedOrganization?.name || matchedOrganization?.displayName || organizationContext.organizationSlug || 'No organization';
          setOrganizationDisplayName(String(resolvedName));
          return;
        }
      } catch (error) {
        console.warn('Unable to load organization display name', error);
      }

      setOrganizationDisplayName(organizationContext.organizationSlug || 'No organization');
    };

    void loadOrganizationDisplayName();
  }, [getAccessTokenSilently, isAuthenticated, organizationContext?.organizationId, organizationContext?.organizationSlug]);

  useEffect(() => {
    if (!isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isAuthenticated) {
      setMembershipRoles([]);
      setMembershipResolved(true);
      return;
    }

    let cancelled = false;
    setMembershipResolved(false);

    const loadMembershipRoles = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${config.apiBaseUrl}/api/organizations/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          return;
        }

        const organizations = await response.json();
        if (!cancelled) {
          setMembershipRoles(getMembershipRoles(organizations));

          const currentContext = getOrganizationContext();
          if (!currentContext?.organizationId && Array.isArray(organizations) && organizations.length > 0) {
            const fallbackOrganization = organizations.find((item: any) => Boolean(item?.isDefault)) ?? organizations[0];
            if (fallbackOrganization?.organizationId && fallbackOrganization?.organizationSlug) {
              setOrganizationContext({
                organizationId: Number(fallbackOrganization.organizationId),
                organizationSlug: String(fallbackOrganization.organizationSlug),
                organizationName: String(fallbackOrganization.organizationName || fallbackOrganization.name || fallbackOrganization.displayName || ''),
              });
            }
          }

          setMembershipResolved(true);
        }
      } catch (error) {
        console.warn('Unable to load organization membership roles for admin layout', error);
        if (!cancelled) {
          setMembershipResolved(true);
        }
      }
    };

    void loadMembershipRoles();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setUserDisplayName('');
      return;
    }

    const resolvedUserName =
      user.name ||
      user.nickname ||
      user.email ||
      'Signed in user';

    setUserDisplayName(String(resolvedUserName));
  }, [isAuthenticated, user]);

  const normalizedPath = useMemo(() => location.pathname.toLowerCase(), [location.pathname]);

  const isLinkActive = (to: string) => {
    const normalizedTo = to.toLowerCase();
    if (normalizedPath === normalizedTo) {
      return true;
    }

    return normalizedPath.startsWith(`${normalizedTo}/`);
  };

  return (
    <div className="admin-shell">
      {isMobile && (
        <header className="admin-mobile-topbar">
          <button
            type="button"
            className="admin-mobile-menu-btn"
            onClick={() => setSidebarOpen(prev => !prev)}
            aria-label="Toggle admin navigation"
          >
            {sidebarOpen ? 'Close' : 'Menu'}
          </button>
          <div className="admin-mobile-title">Admin Console</div>
          <Link to="/" className="admin-mobile-back-link">Main Site</Link>
        </header>
      )}

      <div
        className={`admin-sidebar-backdrop ${sidebarOpen ? 'is-open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      <aside className={`admin-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-title">Admin Console</div>
          <div className="admin-sidebar-subtitle">Unified management workspace</div>
          <div style={{ marginTop: 8, fontSize: '0.84rem', fontWeight: 700, color: '#dce8ff', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ opacity: 0.8 }}>Org:</span>
              <span>{organizationDisplayName || 'Loading...'}</span>
            </div>
            {userDisplayName && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#e8f1ff' }}>
                <span style={{ opacity: 0.8 }}>User:</span>
                <span>{userDisplayName}</span>
              </div>
            )}
          </div>
          <Link to="/" className="admin-back-link">Back to main site</Link>
        </div>

        <AdminSidebarNavigation
          visibleSections={visibleSections}
          isLinkActive={isLinkActive}
          isMobile={isMobile}
          onNavigate={() => setSidebarOpen(false)}
        />

        <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: 'auto' }}>
          <button
            type="button"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            style={{
              width: '100%',
              padding: '8px 12px',
              background: 'rgba(220,38,38,0.15)',
              border: '1px solid rgba(220,38,38,0.4)',
              borderRadius: 6,
              color: '#fca5a5',
              fontSize: '0.84rem',
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            Sign Out
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <div className="admin-content">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
